import { useCallback, useEffect, useRef, useState } from "react";
import { type ClipboardItem, api } from "../lib/api.ts";
import { socketService } from "../lib/socket.ts";

interface Props {
  syncKey: string;
}

interface SpeechRecognitionAlternative {
  transcript: string;
}

interface SpeechRecognitionResult {
  isFinal: boolean;
  0: SpeechRecognitionAlternative;
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResult>;
}

interface SpeechRecognitionErrorEventLike {
  error: string;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const maybeCtor = (
    window as typeof window & {
      SpeechRecognition?: SpeechRecognitionCtor;
      webkitSpeechRecognition?: SpeechRecognitionCtor;
    }
  );
  return maybeCtor.SpeechRecognition ?? maybeCtor.webkitSpeechRecognition ?? null;
}

function appendChunk(current: string, chunk: string): string {
  const cleanChunk = chunk.trim();
  if (!cleanChunk) return current;
  const base = current.trimEnd();
  if (!base) return cleanChunk;
  return `${base} ${cleanChunk}`;
}

export default function Clipboard({ syncKey }: Props) {
  const [items, setItems] = useState<ClipboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [pushText, setPushText] = useState("");
  const [pushing, setPushing] = useState(false);
  const [micListening, setMicListening] = useState(false);
  const [micInterim, setMicInterim] = useState("");
  const [micError, setMicError] = useState("");
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState<Record<string, boolean>>({});
  const [speechSupported] = useState(() => Boolean(getSpeechRecognitionCtor()));
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const shouldKeepListeningRef = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    api.clipboard
      .list(syncKey)
      .then((r) => {
        setItems(r.items);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [syncKey]);

  useEffect(() => {
    load();
    return socketService.on("clipboard:push", () => load());
  }, [load]);

  useEffect(() => {
    return () => {
      shouldKeepListeningRef.current = false;
      recognitionRef.current?.stop();
    };
  }, []);

  async function push() {
    if (!pushText.trim()) return;
    setPushing(true);
    await api.clipboard.push(syncKey, pushText.trim()).catch(() => {});
    setPushText("");
    setPushing(false);
    load();
  }

  async function copyItem(itemId: string, content: string) {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedId(itemId);
      window.setTimeout(() => {
        setCopiedId((curr) => (curr === itemId ? null : curr));
      }, 1100);
    } catch {
      // ignore
    }
  }

  async function deleteItem(itemId: string) {
    setDeleteBusy((prev) => ({ ...prev, [itemId]: true }));
    try {
      await api.clipboard.delete(syncKey, itemId);
      setItems((prev) => prev.filter((item) => item.id !== itemId));
    } catch {
      // ignore
    } finally {
      setDeleteBusy((prev) => ({ ...prev, [itemId]: false }));
    }
  }

  function getRecognition(): SpeechRecognitionLike | null {
    if (recognitionRef.current) return recognitionRef.current;
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) return null;

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    recognition.onresult = (event) => {
      let finalChunk = "";
      let interimChunk = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (!transcript) continue;
        if (result.isFinal) {
          finalChunk += `${transcript} `;
        } else {
          interimChunk += `${transcript} `;
        }
      }

      const finalText = finalChunk.trim();
      if (finalText) {
        setPushText((prev) => appendChunk(prev, finalText));
      }
      setMicInterim(interimChunk.trim());
    };
    recognition.onerror = (event) => {
      setMicError(event.error ? `Mic error: ${event.error}` : "Mic error");
      shouldKeepListeningRef.current = false;
      setMicListening(false);
      setMicInterim("");
    };
    recognition.onend = () => {
      if (shouldKeepListeningRef.current) {
        try {
          recognition.start();
          return;
        } catch {
          // fall through to stop state
        }
      }
      setMicListening(false);
      setMicInterim("");
    };

    recognitionRef.current = recognition;
    return recognition;
  }

  function startMic() {
    const recognition = getRecognition();
    if (!recognition) {
      setMicError("Speech recognition is not supported in this browser");
      return;
    }
    setMicError("");
    shouldKeepListeningRef.current = true;
    setMicListening(true);
    try {
      recognition.start();
    } catch {
      // Already running
    }
  }

  function stopMic() {
    shouldKeepListeningRef.current = false;
    setMicListening(false);
    setMicInterim("");
    recognitionRef.current?.stop();
  }

  return (
    <div className="page">
      <h1>Clipboard</h1>
      <div className="push-row">
        <textarea
          rows={2}
          placeholder="Push text to all surfaces…"
          value={pushText}
          onChange={(e) => setPushText(e.target.value)}
        />
        <div className="push-actions">
          <button
            type="button"
            className={`mic-btn ${micListening ? "mic-btn--active" : ""}`}
            onClick={micListening ? stopMic : startMic}
            disabled={!speechSupported}
            title={speechSupported ? "Speak to fill text" : "Speech recognition not supported"}
          >
            <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" focusable="false">
              <path
                d="M12 15a4 4 0 0 0 4-4V7a4 4 0 1 0-8 0v4a4 4 0 0 0 4 4Zm-6-4a1 1 0 1 1 2 0 4 4 0 0 0 8 0 1 1 0 1 1 2 0 6 6 0 0 1-5 5.91V20h2a1 1 0 1 1 0 2H9a1 1 0 1 1 0-2h2v-3.09A6 6 0 0 1 6 11Z"
                fill="currentColor"
              />
            </svg>
            {micListening ? "Stop" : "Mic"}
          </button>
          <button type="button" onClick={push} disabled={pushing || !pushText.trim()}>
            {pushing ? "Pushing…" : "Push"}
          </button>
        </div>
      </div>
      {micInterim && <p className="mic-status">Listening: {micInterim}</p>}
      {micError && <p className="error-text">{micError}</p>}
      {loading ? (
        <div className="loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">No clipboard items. Push something or copy on your desktop.</div>
      ) : (
        <ul className="clip-list">
          {items.map((item) => (
            <li key={item.id} className="clip-item">
              <pre className="clip-item__content">{item.content.slice(0, 300)}</pre>
              <div className="clip-item__meta">
                <span>{item.contentType}</span>
                <span>{new Date(item.createdAt * 1000).toLocaleTimeString()}</span>
                <div className="clip-item__actions">
                  <button type="button" onClick={() => copyItem(item.id, item.content)}>
                    {copiedId === item.id ? "Copied" : "Copy"}
                  </button>
                  <button
                    type="button"
                    className="clip-icon-btn"
                    onClick={() => deleteItem(item.id)}
                    disabled={Boolean(deleteBusy[item.id])}
                    aria-label="Delete clipboard item"
                    title="Delete"
                  >
                    {deleteBusy[item.id] ? "..." : "🗑"}
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
